import { useMemo } from 'react';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import { useEWallets } from '@/hooks/useEWallets';
import { useCardTerminals } from '@/hooks/useCardTerminals';
import { useGeneralConfig } from '@/hooks/useGeneralConfig';
import type { PaymentMethod } from '../types';

export interface AvailablePaymentMethods {
  /** Luôn bắt đầu bằng 'cash'; thêm bank/wallet/card nếu nhóm có ≥1 tài khoản enabled. */
  methods: PaymentMethod[];
  isLoading: boolean;
}

/**
 * TCR-1428 — hình thức hiện ở POS khi tenant chưa khai `pos_payment_methods`.
 *
 * KHÔNG có 'wallet': ví điện tử không được dùng, nhưng ví cũ vẫn nằm trong DB
 * nên POS cứ hiện tab "Ví ĐT". Fallback này còn che khoảng BE chưa deploy field
 * mới — nếu để mặc định gồm 'wallet' thì tab sẽ loé lên rồi biến mất sau khi
 * BE lên, đúng thứ ticket muốn ẩn.
 *
 * Phải khớp `default` của `pos_payment_methods` ở
 * `tenant-settings.schema.ts` (shared) và `general-settings.service.ts` (BE).
 */
const DEFAULT_ALLOWED_METHODS: readonly PaymentMethod[] = ['cash', 'bank', 'card'];

function hasEnabled(list: ReadonlyArray<{ enabled?: boolean }> | undefined): boolean {
  return (list ?? []).some((a) => a.enabled !== false);
}

export function useAvailablePaymentMethods(): AvailablePaymentMethods {
  const bank = useBankAccounts();
  const wallet = useEWallets();
  const card = useCardTerminals();
  const general = useGeneralConfig();

  const allowed = general.data?.pos_payment_methods;

  const methods = useMemo<PaymentMethod[]>(() => {
    // 'cash' KHÔNG đi qua bộ lọc: cấu hình sai (bỏ tick hết, hoặc quên cash)
    // không được phép để thu ngân không còn hình thức nào mà bấm.
    const allowList = new Set<string>(allowed ?? DEFAULT_ALLOWED_METHODS);
    const result: PaymentMethod[] = ['cash'];
    if (allowList.has('bank') && hasEnabled(bank.data)) result.push('bank');
    if (allowList.has('wallet') && hasEnabled(wallet.data)) result.push('wallet');
    if (allowList.has('card') && hasEnabled(card.data)) result.push('card');
    return result;
  }, [bank.data, wallet.data, card.data, allowed]);

  return {
    methods,
    // Gộp cả `general`: chờ biết chắc cấu hình rồi hãy dựng tab. `PaymentMethodFields`
    // dùng cờ này để hoãn việc reset method đang chọn về 'cash'.
    isLoading: bank.isLoading || wallet.isLoading || card.isLoading || general.isLoading,
  };
}
